import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Car, Loader2, Plus, Trash2, Check, X, AlertTriangle, ArrowLeft, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import Footer from './Footer';

interface Spot {
  id: string;
  label: string | null;
  licensePlate: string | null;
  driverName: string | null;
  driverEmail: string | null;
  driverPhone: string | null;
  status: 'active' | 'pending_removal' | 'removed';
}

interface Group {
  id: string;
  entityName: string;
  billingEmail: string;
  contactName: string;
  contactPhone: string | null;
  unitAmount: number;
  quantity: number;
  monthlyTotal: number;
  status: 'pending' | 'active' | 'past_due' | 'cancelled';
  scheduledStartDate: string | null;
  currentPeriodEnd: string | null;
  cancelledAt: string | null;
  lot: { displayName?: string; slug?: string };
  spots: Spot[];
}

const apiUrl = () =>
  window.location.hostname === 'localhost'
    ? '/api/manage-subscription-group'
    : '/.netlify/functions/manage-subscription-group';

/**
 * Self-serve page for an entity that pays for several monthly spots on one card.
 * Reached via the opaque token link emailed to the billing contact — card
 * details and invoices stay in the Stripe customer portal.
 */
function ManageSubscription() {
  const { token } = useParams<{ token: string }>();

  const [group, setGroup] = useState<Group | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editingSpotId, setEditingSpotId] = useState<string | null>(null);
  const [editPlate, setEditPlate] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newPlate, setNewPlate] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);

  const callApi = useCallback(
    async (payload: Record<string, unknown>) => {
      const response = await fetch(apiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...payload })
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Something went wrong');
      }
      return result as { group: Group; message?: string };
    },
    [token]
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const result = await callApi({ action: 'get' });
        if (!cancelled) setGroup(result.group);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Unable to load subscription');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [callApi]);

  const runAction = async (payload: Record<string, unknown>, fallbackMessage: string) => {
    setBusy(true);
    try {
      const result = await callApi(payload);
      setGroup(result.group);
      toast.success(result.message || fallbackMessage);
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : fallbackMessage);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveSpot = async (spotId: string) => {
    const plate = editPlate.trim().toUpperCase();
    if (!plate) {
      toast.error('License plate is required');
      return;
    }
    const saved = await runAction(
      { action: 'update_spot', spotId, licensePlate: plate },
      'Spot updated'
    );
    if (saved) setEditingSpotId(null);
  };

  const addSpot = async () => {
    const plate = newPlate.trim().toUpperCase();
    if (!plate) {
      toast.error('License plate is required');
      return;
    }
    const added = await runAction({ action: 'add_spot', licensePlate: plate }, 'Spot added');
    if (added) {
      setNewPlate('');
      setIsAdding(false);
    }
  };

  const removeSpot = async (spotId: string) => {
    await runAction({ action: 'remove_spot', spotId }, 'Spot scheduled for removal');
  };

  const cancelGroup = async () => {
    const cancelled = await runAction({ action: 'cancel_group' }, 'Subscription cancelled');
    if (cancelled) setConfirmCancel(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your subscription...</p>
        </div>
      </div>
    );
  }

  if (loadError || !group) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Subscription not found</h2>
          <p className="text-gray-600 mb-4">
            {loadError || 'This management link is no longer valid. Check your receipt email for the latest link.'}
          </p>
          <Link to="/" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Return home
          </Link>
        </div>
      </div>
    );
  }

  const activeSpots = group.spots.filter((spot) => spot.status === 'active');
  const isCancelled = group.status === 'cancelled';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg p-6 my-8 space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <Building2 size={16} />
            <span>{group.lot.displayName}</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{group.entityName}</h1>
          <p className="text-gray-600 mt-1">
            {activeSpots.length} monthly spot{activeSpots.length === 1 ? '' : 's'} · $
            {group.monthlyTotal.toFixed(2)}/month · billed to {group.billingEmail}
          </p>
          {group.currentPeriodEnd && !isCancelled && (
            <p className="text-sm text-gray-500 mt-1">
              Next renewal {format(new Date(group.currentPeriodEnd), 'MMMM d, yyyy')}
            </p>
          )}
        </div>

        {isCancelled && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            This subscription is cancelled
            {group.cancelledAt ? ` and ends ${format(new Date(group.cancelledAt), 'MMMM d, yyyy')}` : ''}.
            Spots stay parkable until then.
          </div>
        )}

        {group.status === 'past_due' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
            The last payment failed. Update your card in the billing portal link from your receipt email.
          </div>
        )}

        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Spots</h2>

          {group.spots.map((spot, index) => (
            <div
              key={spot.id}
              className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${
                spot.status === 'pending_removal' ? 'border-gray-200 bg-gray-50' : 'border-gray-300'
              }`}
            >
              <Car className="text-gray-400 shrink-0" size={20} />

              {editingSpotId === spot.id ? (
                <>
                  <input
                    type="text"
                    value={editPlate}
                    onChange={(e) => setEditPlate(e.target.value)}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="License plate"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => saveSpot(spot.id)}
                    disabled={busy}
                    className="text-green-600 hover:text-green-700 disabled:opacity-50"
                    aria-label="Save plate"
                  >
                    <Check size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingSpotId(null)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Cancel edit"
                  >
                    <X size={20} />
                  </button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {spot.licensePlate || 'No plate assigned'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {spot.label || `Spot ${index + 1}`}
                      {spot.status === 'pending_removal' && ' · ends at the end of this billing period'}
                    </p>
                  </div>

                  {!isCancelled && spot.status === 'active' && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingSpotId(spot.id);
                          setEditPlate(spot.licensePlate || '');
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Change plate
                      </button>
                      {activeSpots.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSpot(spot.id)}
                          disabled={busy}
                          className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                          aria-label="Remove spot"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          ))}

          {!isCancelled && (isAdding ? (
            <div className="flex items-center gap-3 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3">
              <Car className="text-gray-400 shrink-0" size={20} />
              <input
                type="text"
                value={newPlate}
                onChange={(e) => setNewPlate(e.target.value)}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="License plate"
                autoFocus
              />
              <button
                type="button"
                onClick={addSpot}
                disabled={busy}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? 'Adding...' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsAdding(false);
                  setNewPlate('');
                }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Cancel add"
              >
                <X size={20} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm font-medium text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add a spot (${group.unitAmount.toFixed(2)}/month, prorated today)
            </button>
          ))}
        </div>

        {!isCancelled && (
          <div className="border-t border-gray-200 pt-4">
            {confirmCancel ? (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 space-y-3">
                <p className="text-sm text-red-700">
                  Cancel all {activeSpots.length} spot{activeSpots.length === 1 ? '' : 's'}? Parking stays
                  active until the end of the period you've already paid for.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={cancelGroup}
                    disabled={busy}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {busy ? 'Cancelling...' : 'Yes, cancel'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Keep subscription
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmCancel(true)}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Cancel this subscription
              </button>
            )}
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}

export default ManageSubscription;
